import { Component, Input } from '@angular/core';

/** Pieza del logo: las tres sueltas del hexágono + el hexágono completo. */
export type PieceKey = 'full' | 'tr' | 'tl' | 'bl' | 'br';

/**
 * Paths de las piezas del logo TodoSector (mismos vectores/pestañas que el
 * puzzle del hero). `full` = hexágono completo (4 piezas con junta blanca).
 * Extraídos del handoff de diseño (`ts-piece.js`).
 */
const PIECE_PATHS: Record<Exclude<PieceKey, 'full'>, string> = {
  tr: 'M 102.88 8 L 114.38 11.59 L 171.16 44.66 L 179.78 52.56 L 181.94 56.88 L 183.38 62.63 L 183.38 97.13 L 155.34 98.56 L 152.47 102.88 L 155.34 112.94 L 155.34 118.69 L 151.03 123 L 146 123.72 L 143.13 122.28 L 139.53 117.25 L 143.13 104.31 L 142.41 101.44 L 139.53 98.56 L 102.88 97.84 L 102.88 62.63 L 105.03 61.91 L 112.22 64.78 L 119.41 64.78 L 125.88 60.47 L 128.75 54 L 128.03 47.53 L 122.28 41.06 L 119.41 39.63 L 112.94 39.63 L 105.75 42.5 L 102.88 41.78 L 102.88 8 Z',
  tl: 'M 94.97 8 L 97.84 8.72 L 97.84 43.22 L 99.28 45.38 L 102.16 47.53 L 106.47 47.53 L 112.94 44.66 L 118.69 44.66 L 123.72 50.41 L 123 56.16 L 119.41 59.75 L 114.38 60.47 L 107.19 57.59 L 101.44 57.59 L 98.56 60.47 L 97.84 96.41 L 96.41 97.84 L 62.63 97.13 L 61.91 95.69 L 65.5 84.91 L 64.78 79.88 L 58.31 72.69 L 51.13 71.25 L 45.38 73.41 L 40.34 79.16 L 39.63 87.06 L 42.5 94.25 L 41.78 97.13 L 16.63 97.13 L 16.63 64.06 L 18.06 57.59 L 21.66 51.13 L 26.69 46.09 L 36.03 40.34 L 87.06 10.88 L 94.97 8 Z',
  bl: 'M 51.84 76.28 L 56.88 77.72 L 60.47 82.03 L 60.47 86.34 L 57.59 93.53 L 57.59 98.56 L 60.47 101.44 L 64.78 102.88 L 97.13 102.88 L 97.84 104.31 L 97.13 137.38 L 94.97 138.09 L 87.78 135.22 L 79.16 135.94 L 73.41 140.97 L 71.25 147.44 L 73.41 154.63 L 80.59 160.38 L 87.78 160.38 L 94.97 157.5 L 97.13 158.22 L 97.13 192 L 87.06 189.13 L 31 156.78 L 21.66 148.88 L 18.78 143.84 L 16.63 136.66 L 16.63 102.88 L 43.94 102.16 L 47.53 97.84 L 47.53 93.53 L 44.66 87.06 L 44.66 82.03 L 48.25 77.72 L 51.84 76.28 Z',
  br: 'M 102.88 102.88 L 137.38 102.88 L 138.09 105.75 L 135.22 112.94 L 135.22 119.41 L 140.97 126.59 L 145.28 128.75 L 153.91 127.31 L 158.22 123.72 L 160.38 119.41 L 160.38 112.22 L 157.5 105.03 L 158.22 102.88 L 183.38 102.88 L 183.38 137.38 L 181.94 143.13 L 177.63 150.31 L 172.59 154.63 L 148.16 169 L 114.38 188.41 L 105.75 192 L 102.88 192 L 102.88 159.66 L 101.44 155.34 L 97.84 152.47 L 94.25 152.47 L 87.78 155.34 L 81.31 155.34 L 77 150.31 L 77 144.56 L 81.31 140.25 L 85.63 139.53 L 95.69 143.13 L 101.44 140.25 L 102.88 135.94 L 102.88 102.88 Z',
};

const VIEWBOX: Record<PieceKey, string> = {
  tr: '95 1 95 129',
  tl: '10 2 119 101',
  bl: '10 70 95 128',
  br: '69 96 121 102',
  full: '10 2 180 196',
};

/**
 * Pieza del logo TodoSector como icono reutilizable del sistema visual de
 * "Soluciones". Diccionario fijo módulo→color (tokens `--ts-piece-*`):
 *   - `full`  (color `--ts-primary`) → Base Esencial / marca.
 *   - `tr`    (color del módulo)     → un módulo.
 *   - `tr` + `variant="outline"`     → un complemento (mismo color, en contorno).
 *
 * DOM propio sin encapsular a propósito (como `hex-icon`) para que los
 * `var(--ts-*)` del tema resuelvan sobre el color/trazo.
 */
@Component({
  selector: 'app-ts-piece',
  standalone: true,
  template: `
    <svg
      [attr.viewBox]="viewBox"
      [attr.width]="size"
      [attr.height]="size"
      style="display:block; overflow:visible;"
      aria-hidden="true">
      @for (d of paths; track $index) {
        <path
          [attr.d]="d"
          [attr.fill]="fillAttr"
          [attr.stroke]="strokeAttr"
          [attr.stroke-width]="strokeWidth"
          stroke-linejoin="round"
          stroke-linecap="round" />
      }
    </svg>
  `,
  styles: [
    `
      :host {
        display: inline-block;
        line-height: 0;
      }
    `,
  ],
})
export class TsPieceComponent {
  /** Qué pieza dibujar. */
  @Input() piece: PieceKey = 'tr';
  /** Color de relleno/trazo (CSS color o `var(--ts-piece-*)`). */
  @Input() color = 'var(--ts-primary)';
  /** Lado del icono en px. */
  @Input() size: number | string = 18;
  /** Complemento: pieza en contorno (sin relleno). */
  @Input() variant: 'solid' | 'outline' = 'solid';

  get isFull(): boolean {
    return this.piece === 'full';
  }
  get isOutline(): boolean {
    return this.variant === 'outline' && !this.isFull;
  }
  get viewBox(): string {
    return VIEWBOX[this.piece] ?? VIEWBOX.tr;
  }
  /** El hexágono completo son sus 4 piezas con junta blanca; el resto, una. */
  get paths(): string[] {
    if (this.isFull) {
      return [PIECE_PATHS.tl, PIECE_PATHS.tr, PIECE_PATHS.bl, PIECE_PATHS.br];
    }
    return [PIECE_PATHS[this.piece as Exclude<PieceKey, 'full'>] ?? PIECE_PATHS.tr];
  }
  get fillAttr(): string {
    return this.isOutline ? 'none' : this.color;
  }
  get strokeAttr(): string {
    // El hexágono completo lleva junta blanca entre piezas, como el logo.
    return this.isFull ? '#ffffff' : this.color;
  }
  get strokeWidth(): number {
    if (this.isFull) return 1;
    return this.isOutline ? 7 : 3;
  }
}
